import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { RatingPrompt } from "../components/RatingPrompt";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { DashboardMenu } from "../components/DashboardMenu";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, LogOut, Heart, Clock, History, Star,
  Play, ChevronRight, ChevronLeft, Pause, BarChart3, Bell, BellRing,
  Trash2, CalendarClock, Settings, User, BookOpen, ShieldCheck, X, Menu, Lock,
  Brain, Waves, TreePine, Scroll, Mic, Moon, CloudSun, Headphones,
  Shield, HandHeart, Flame, Target, Sprout, PersonStanding, HelpCircle, Flower2,
  LayoutGrid, UserCircle, Plane, Bed, Box, Users2, HandMetal
} from "lucide-react";
import { format } from "date-fns";
import logger from "../lib/logger";
import useSeo from "../hooks/useSeo";
import { CalmMomentsCard } from "../components/CalmMomentsCard";
import { TrialCountdownBanner } from "../components/TrialCountdownBanner";
import { BillingIssueBanner } from "../components/BillingIssueBanner";
import { DailyMeditationCard } from "../components/DailyMeditationCard";
import { ShareInviteButton } from "../components/ShareInviteButton";
import { StreakPill } from "../components/StreakPill";
import { StreakCelebrationModal } from "../components/StreakCelebrationModal";
import { SiteFooter } from "../components/SiteFooter";
import { VipBadge } from "../components/VipBadge";
import { useBilling } from "../contexts/BillingContext";
import { getStreak, markCelebrated } from "../lib/streak";
import { LIBRARY_PRACTICES } from "../lib/libraryPractices";
import { TRIAL_ACCESS_IDS, isLockedForUser } from "../lib/trialAccess";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERVENTION_ICONS = {
  quiet_the_worry: "🧘",
  overcome_overwhelm: "🌊",
  dial_down_tension: "💆",
  healing_old_stories: "📖",
  beat_stage_fright: "🎭",
  fear_of_sleeplessness: "🌙",
  shake_off_sadness: "🌤",
  boost_self_worth: "💪",
  hold_boundaries: "🛡",
  relief_from_grief: "🕊",
  calm_your_anger: "🔥",
  enhance_performance: "🎯",
  new_habit_new_you: "🌱",
  headache_relief: "💆",
  nightmare_reset: "🌙"
};

// Curated lucide icon + accent palette per intervention — used for the dashboard tiles
// to replace the photo imagery with a cohesive, professional iconography system.
const INTERVENTION_VISUALS = {
  quiet_the_worry:       { icon: Brain,     tint: "#3090A8", from: "#C7D9C3", to: "#9EBBA0", photo: "/images/interventions/quiet-the-worry.webp" },
  overcome_overwhelm:    { icon: Waves,     tint: "#78C0D8", from: "#CEDAF2", to: "#A4B8E0", photo: "/images/interventions/overcome-overwhelm.webp" },
  dial_down_tension:     { icon: PersonStanding, tint: "#9AAFC8", from: "#D5DCE7", to: "#B0C0D6", photo: "/images/interventions/dial-down-tension.webp" },
  healing_old_stories:   { icon: Scroll,    tint: "#B8965A", from: "#EDDFC0", to: "#D6B87C", photo: "/images/interventions/healing-old-stories.webp" },
  beat_stage_fright:     { icon: Mic,       tint: "#C17B9B", from: "#EBD1DD", to: "#D199B4", photo: "/images/interventions/beat-stage-fright.webp" },
  fear_of_sleeplessness: { icon: Moon,      tint: "#6B7AA8", from: "#CCD2E4", to: "#9099BF", photo: "/images/interventions/fear-of-sleeplessness.webp" },
  shake_off_sadness:     { icon: CloudSun,  tint: "#E2B887", from: "#F6E2C5", to: "#ECC99C", photo: "/images/interventions/shake-off-sadness.webp" },
  boost_self_worth:      { icon: Star,      tint: "#D98880", from: "#F1CBC6", to: "#E1A19A", photo: "/images/interventions/boost-self-worth.webp" },
  hold_boundaries:       { icon: Shield,    tint: "#7A9A7E", from: "#C6D6C8", to: "#94AD97", photo: "/images/interventions/hold-boundaries.webp" },
  relief_from_grief:     { icon: HandHeart, tint: "#9BAFA7", from: "#D1DBD6", to: "#AFBFB8", photo: "/images/interventions/relief-from-grief.webp" },
  calm_your_anger:       { icon: Flame,     tint: "#C4746A", from: "#EECAC3", to: "#D58B81", photo: "/images/interventions/calm-your-anger.webp" },
  enhance_performance:   { icon: Target,    tint: "#6B8E8A", from: "#C0D0CE", to: "#8AA8A4", photo: "/images/interventions/enhance-performance.webp" },
  new_habit_new_you:     { icon: Sprout,    tint: "#8FB07A", from: "#CFE0C2", to: "#A5C195", photo: "/images/interventions/new-habit-new-you.webp" },
  headache_relief:       { icon: PersonStanding, tint: "#A48E6B", from: "#E4DCC8", to: "#C9B991", photo: "/images/interventions/headache-relief.webp" },
  nightmare_reset:       { icon: Moon,      tint: "#5C6F92", from: "#C8CFE0", to: "#8E9CBE", photo: "/images/interventions/nightmare-reset.webp" },
  fear_of_flying:        { icon: Plane,     tint: "#78C0D8", from: "#D6E5F0", to: "#A8C8DD", photo: "https://customer-assets.emergentagent.com/job_calm-process/artifacts/80h47om1_john-mcarthur-PrdNTrIrG8w-unsplash.jpg" },
  cpap_claustrophobia:   { icon: Bed,       tint: "#6B7AA8", from: "#CCD2E4", to: "#9099BF", photo: "https://customer-assets.emergentagent.com/job_calm-process/artifacts/952bn3jh_woman-wearing-anti-snoring-chin-straps%20%281%29.jpg" },
  fear_of_small_places:  { icon: Box,       tint: "#A48E6B", from: "#E4DCC8", to: "#C9B991", photo: "https://images.unsplash.com/photo-1611940273499-e7e8ed5cbb55?auto=format&fit=crop&q=80&w=800" },
  new_experience_anxiety:{ icon: Users2,    tint: "#9F7AEA", from: "#DBCCF5", to: "#B89AE6", photo: "https://customer-assets.emergentagent.com/job_calm-process/artifacts/3er0ns0m_mitchell-luo-1zTxyQ3kMaQ-unsplash%20%281%29.jpg" },
  resist_an_urge:        { icon: HandMetal, tint: "#D98880", from: "#F1CBC6", to: "#E1A19A", photo: "https://customer-assets.emergentagent.com/job_calm-process/artifacts/kysyitg9_bermix-studio-rGT7TZ7gBdo-unsplash.jpg" },
};

const INTERVENTION_TITLES = {
  quiet_the_worry: "Quiet The Worry",
  overcome_overwhelm: "Overcome Your Overwhelm",
  dial_down_tension: "Dial Down Body Tension",
  healing_old_stories: "Healing Old Stories To Let Them Go",
  beat_stage_fright: "Beat Stage Fright",
  fear_of_sleeplessness: "Fear Of Sleeplessness",
  shake_off_sadness: "Shake Off The Blues",
  boost_self_worth: "Boost Your Self-Worth",
  hold_boundaries: "Hold Your Boundaries",
  relief_from_grief: "Relief From Grief",
  calm_your_anger: "Calm Your Anger",
  enhance_performance: "Enhance Your Performance",
  new_habit_new_you: "New Habit, New You",
  headache_relief: "Headache Relief",
  nightmare_reset: "Nightmare Reset"
};

// Category pills shown above the swipe-carousel. Each tab has its own icon +
// accent palette so users can scan and pick visually. Maps to backend group
// keys plus a synthetic "library" channel for audio Daily Practices.
// During the free trial, users only get access to a small starter set —
// everything else displays a "Unlocks with paid subscription" lock badge.
// TRIAL_ACCESS_IDS lives in lib/trialAccess.js (imported at top) so the
// Daily Meditation hero and any future surface share the same lock rule.

const CATEGORY_TABS = [
  { key: "emotional",      label: "Emotional Grounding",     icon: HandHeart, accent: "#1B2349", soft: "#E4E7EE" },
  { key: "physical",       label: "Calm Body",               icon: Waves,     accent: "#306090", soft: "#DDE4F0" },
  { key: "sleep",          label: "Sleep Health",            icon: Moon,      accent: "#5C6F92", soft: "#DDE2EE" },
  { key: "growth",         label: "Growth & Confidence",     icon: Sprout,    accent: "#3090A8", soft: "#DBEDF1" },
  { key: "transformation", label: "Repair & Transformation", icon: Sparkles,  accent: "#78C0D8", soft: "#E1F2F7" },
  { key: "mindfulness",    label: "Mindfulness Exercises",   icon: Flower2,   accent: "#7A9A7E", soft: "#E5EEE6" },
  { key: "library",        label: "Ambient Sounds for Recovery", icon: Headphones, accent: "#A8D8D8", soft: "#EFF8F8" },
];

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [interventions, setInterventions] = useState([]);
  const [groups, setGroups] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reminders, setReminders] = useState([]);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [reminderType, setReminderType] = useState("");
  const [reminderDate, setReminderDate] = useState("");
  const [reminderTime, setReminderTime] = useState("");
  const [notifPermission, setNotifPermission] = useState("default");
  // Confirm dialog for abandoning an in-progress intervention
  const [pendingProgressDelete, setPendingProgressDelete] = useState(null);
  const [abandoning, setAbandoning] = useState(false);
  // Billing — drives which interventions / library items are accessible
  // during the free trial. Trial users only get a small whitelist; once
  // status flips to "active" or "waived" the whole library unlocks.
  // Sourced from the shared BillingContext (single fetch at sign-in)
  // rather than a duplicate /api/billing/status request per page.
  const { billing } = useBilling();
  // Practice-streak state. Fetched on mount so the header pill always
  // reflects the latest count; the pill is friendly at 0 too. If a
  // milestone was reached on the *previous* completion (last visit),
  // `celebratingMilestone` is populated so the celebration modal fires
  // exactly once — the ack POST removes it from the celebrated list
  // server-side so it never re-fires on subsequent Dashboard visits.
  const [streak, setStreak] = useState(null);
  const [celebratingMilestone, setCelebratingMilestone] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = await getStreak();
      if (cancelled || !s) return;
      setStreak(s);
      // Determine an *uncelebrated* milestone that the user has reached
      // right now. If the current streak matches one of the milestones
      // and it hasn't been acknowledged, show the modal on Dashboard
      // mount. Only the largest eligible milestone is shown (matches
      // server-side logic in POST /streak/event).
      const MILESTONES = [3, 7, 14, 30];
      const acked = new Set(s.celebrated || []);
      const eligible = MILESTONES.filter(
        (m) => (s.current || 0) >= m && !acked.has(m)
      );
      if (eligible.length) {
        setCelebratingMilestone(Math.max(...eligible));
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const handleCelebrationClose = useCallback(async () => {
    const m = celebratingMilestone;
    setCelebratingMilestone(null);
    if (m) {
      const fresh = await markCelebrated(m);
      if (fresh) setStreak(fresh);
    }
  }, [celebratingMilestone]);

  // Category + ferris-wheel carousel.
  // `activeCategory` is `null` until the user taps a tab — that keeps the
  // dashboard quiet on first load. Both values are persisted across route
  // navigations via sessionStorage so the Back button from the player page
  // returns the user to the same wheel + position.
  const [activeCategory, setActiveCategory] = useState(() => {
    try { return sessionStorage.getItem("dash:activeCategory") || null; }
    catch { return null; }
  });
  const [wheelIndex, setWheelIndex] = useState(() => {
    try {
      const n = Number(sessionStorage.getItem("dash:wheelIndex"));
      return Number.isFinite(n) ? n : 0;
    } catch { return 0; }
  });
  const carouselRef = useRef(null);
  const wheelStageRef = useRef(null);
  // Swipe tracking — touch + mouse drag on the wheel area
  const swipeStartX = useRef(null);
  const swipeHappened = useRef(false);
  const SWIPE_THRESHOLD_PX = 40;

  // Responsive wheel sizing — small monitors get the original 240px card,
  // lg/xl scale up so the carousel actually fills wide screens. Re-evaluates
  // on resize so debug-bar toggles and orientation changes stay accurate.
  const [wheelDims, setWheelDims] = useState(() => {
    if (typeof window === "undefined") return { card: 240, gap: 120 };
    const w = window.innerWidth;
    if (w >= 1536) return { card: 340, gap: 170 };  // 2xl
    if (w >= 1280) return { card: 300, gap: 150 };  // xl
    if (w >= 1024) return { card: 280, gap: 140 };  // lg
    return { card: 240, gap: 120 };
  });
  // App dashboard is private — don't let crawlers index it.
  useSeo({
    title: "Dashboard — Bilateral Calm",
    description: "Your Bilateral Calm dashboard.",
    path: "/dashboard",
    noindex: true,
  });
  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      let next;
      if (w >= 1536) next = { card: 340, gap: 170 };
      else if (w >= 1280) next = { card: 300, gap: 150 };
      else if (w >= 1024) next = { card: 280, gap: 140 };
      else next = { card: 240, gap: 120 };
      setWheelDims((cur) =>
        cur.card === next.card && cur.gap === next.gap ? cur : next
      );
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Persist wheel state on every change so it survives route round-trips.
  useEffect(() => {
    try {
      if (activeCategory) {
        sessionStorage.setItem("dash:activeCategory", activeCategory);
      } else {
        sessionStorage.removeItem("dash:activeCategory");
      }
      sessionStorage.setItem("dash:wheelIndex", String(wheelIndex));
    } catch { /* private mode — silently ignore */ }
  }, [activeCategory, wheelIndex]);

  // When the dashboard re-mounts with a restored category, scroll the wheel
  // into view so the user lands right back on it.
  useEffect(() => {
    if (activeCategory && wheelStageRef.current) {
      wheelStageRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    // run once on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollCarousel = (dir) => {
    // Ferris wheel — advance/retreat the front card.
    setWheelIndex((prev) => prev + dir);
  };

  const onWheelTouchStart = (e) => {
    swipeHappened.current = false;
    swipeStartX.current = e.touches?.[0]?.clientX ?? e.clientX;
  };
  const onWheelTouchEnd = (e) => {
    if (swipeStartX.current == null) return;
    const endX = e.changedTouches?.[0]?.clientX ?? e.clientX;
    const dx = endX - swipeStartX.current;
    swipeStartX.current = null;
    if (Math.abs(dx) > SWIPE_THRESHOLD_PX) {
      swipeHappened.current = true;
      // Swipe left (dx<0) → next card; swipe right (dx>0) → previous
      setWheelIndex((prev) => prev + (dx < 0 ? 1 : -1));
    }
  };

  useEffect(() => {
    fetchData();
    if ("Notification" in window) {
      setNotifPermission(Notification.permission);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll for pending reminders every 30 seconds
  const checkPendingReminders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reminders/pending`, { withCredentials: true });
      if (res.data.length > 0) {
        res.data.forEach(async (rem) => {
          const title = INTERVENTION_TITLES[rem.intervention_type] || rem.intervention_type;
          
          // Show browser notification if permitted
          if ("Notification" in window && Notification.permission === "granted") {
            new Notification("Bilateral Calm Reminder", {
              body: `Time to continue your ${title} session`,
              icon: "/favicon.ico"
            });
          }
          
          // Always show in-app toast
          toast.info(`Reminder: Continue your ${title} session`, {
            action: {
              label: "Go",
              onClick: () => navigate(`/intervention/${rem.intervention_type}`)
            },
            duration: 10000
          });

          // Deactivate the fired reminder
          await axios.put(
            `${API}/reminders/${rem.reminder_id}`,
            { is_active: false },
            { withCredentials: true }
          );
        });
        // Refresh reminders list
        fetchReminders();
      }
    } catch (error) {
      logger.error("Reminder polling failed:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    const interval = setInterval(checkPendingReminders, 30000);
    checkPendingReminders(); // check immediately
    return () => clearInterval(interval);
  }, [checkPendingReminders]);

  // Expired-subscription users are auto-routed to /pricing by the
  // GlobalPaywallGuard mounted at the router root, which enforces the
  // paywall across every locked route (dashboard, library, intervention…).
  // Dashboard itself no longer runs the redirect — see
  // /app/frontend/src/components/GlobalPaywallGuard.jsx.

  const fetchData = async () => {
    try {
      const [interventionsRes, statsRes, groupsRes] = await Promise.all([
        axios.get(`${API}/interventions`, { withCredentials: true }),
        axios.get(`${API}/stats`, { withCredentials: true }),
        axios.get(`${API}/intervention-groups`, { withCredentials: true }),
      ]);
      setInterventions(interventionsRes.data);
      setStats(statsRes.data);
      setGroups(groupsRes.data);
      // Billing status is provided by the shared BillingContext (fetched
      // once at sign-in). Dashboard no longer needs its own fetch.
      fetchReminders();
    } catch (error) {
      logger.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReminders = async () => {
    try {
      const res = await axios.get(`${API}/reminders`, { withCredentials: true });
      setReminders(res.data);
    } catch (error) {
      logger.error("Failed to fetch reminders:", error);
    }
  };

  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) {
      toast.error("Your browser doesn't support notifications");
      return;
    }
    const permission = await Notification.requestPermission();
    setNotifPermission(permission);
    if (permission === "granted") {
      toast.success("Notifications enabled! We'll remind you when it's time.");
    } else {
      toast.info("You can still see reminders in-app even without browser notifications.");
    }
  };

  const handleCreateReminder = async () => {
    if (!reminderType || !reminderDate || !reminderTime) {
      toast.error("Please fill in all reminder fields");
      return;
    }
    try {
      const reminderDateTime = new Date(`${reminderDate}T${reminderTime}`);
      if (reminderDateTime <= new Date()) {
        toast.error("Please select a future date and time");
        return;
      }
      await axios.post(
        `${API}/reminders`,
        {
          intervention_type: reminderType,
          reminder_time: reminderDateTime.toISOString()
        },
        { withCredentials: true }
      );
      toast.success("Reminder set!");
      setShowReminderDialog(false);
      setReminderType("");
      setReminderDate("");
      setReminderTime("");
      fetchReminders();
    } catch (error) {
      toast.error("Failed to create reminder");
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    try {
      await axios.delete(`${API}/reminders/${reminderId}`, { withCredentials: true });
      toast.success("Reminder deleted");
      fetchReminders();
    } catch (error) {
      toast.error("Failed to delete reminder");
    }
  };

  const toggleFavorite = async (type, isFavorite) => {
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${type}`, { withCredentials: true });
      } else {
        await axios.post(`${API}/favorites`, { intervention_type: type }, { withCredentials: true });
      }
      fetchData();
    } catch (error) {
      logger.error("Failed to toggle favorite:", error);
    }
  };

  const startIntervention = (type) => {
    navigate(`/intervention/${type}`);
  };

  // Find the index of the first card in a given category that isn't
  // locked for the current trial state. Used when the user opens a
  // category so the wheel lands on something they can actually tap, not
  // a locked card. Falls back to 0 if every card in the category is
  // locked (so the user still sees the carousel — they just see lock
  // badges).
  const firstUnlockedIndexFor = (tabKey) => {
    const isTrialing =
      billing?.status === "trialing" || billing?.status === "expired";
    if (!isTrialing) return 0;
    let ids = [];
    if (tabKey === "library") {
      ids = LIBRARY_PRACTICES
        .filter((p) => p.category !== "Guided Meditation")
        .map((p) => p.id);
    } else if (tabKey === "mindfulness") {
      ids = LIBRARY_PRACTICES
        .filter((p) => p.category === "Guided Meditation")
        .map((p) => p.id);
    } else {
      ids = (groups[tabKey]?.types) || [];
    }
    const idx = ids.findIndex((id) => TRIAL_ACCESS_IDS.has(id));
    return idx >= 0 ? idx : 0;
  };

  const confirmAbandonProgress = async () => {
    if (!pendingProgressDelete) return;
    setAbandoning(true);
    try {
      await axios.delete(`${API}/progress/${pendingProgressDelete.intervention_type}`, {
        withCredentials: true,
      });
      // Remove from the in-progress list locally so the UI updates immediately
      setStats((prev) => prev ? {
        ...prev,
        in_progress: (prev.in_progress || []).filter(
          (p) => p.progress_id !== pendingProgressDelete.progress_id
        ),
        in_progress_count: Math.max(0, (prev.in_progress_count || 1) - 1),
      } : prev);
      toast.success("In-progress session cleared.");
      setPendingProgressDelete(null);
    } catch (error) {
      logger.error("Abandon progress failed:", error);
      toast.error("Couldn't clear. Try again in a moment.");
    } finally {
      setAbandoning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF8F1] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3090A8]"></div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen relative isolate"
      data-testid="dashboard"
      style={{
        backgroundImage: "url('/images/dashboard/dashboard-wallpaper.webp')",
        backgroundAttachment: "fixed",
        backgroundSize: "140% auto",
        backgroundPosition: "center top",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Rating-prompt modal — checks eligibility once per session and
          renders a 2-step sentiment gate → App Store rating flow. All
          gating is enforced server-side (routers/rating_prompt.py). */}
      <RatingPrompt />
      {/* Cream-tinted overlay so wallpaper sits softly behind the UI without
          fighting the card contrast. Sits below all dashboard content via -z-10. */}
      <div
        aria-hidden
        className="fixed inset-0 -z-10 bg-[#FBF8F1]/30 backdrop-blur-[2px]"
      />
      {/* Billing-issue banner — only visible when RevenueCat (or Stripe) signals
          a failed renewal charge. Rendered ABOVE the trial banner so it takes
          visual priority over trial-status messaging. */}
      <BillingIssueBanner billing={billing} />
      {/* Trial countdown banner — only renders for users in their 5-day trial */}
      <TrialCountdownBanner billing={billing} />
      {/* Header */}
      <header className="glass-header border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-[1440px] xl:max-w-[1680px] mx-auto px-6 lg:px-10 xl:px-14 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img src="/logo-bilateral-calm.png" alt="Bilateral Calm" className="h-28 sm:h-32 w-auto" />
          </div>
          
          <nav className="hidden md:flex items-center gap-6">
            <Link 
              to="/dashboard" 
              className="text-[#1B2349] font-medium hover:text-[#3090A8] transition-colors"
              data-testid="nav-dashboard"
            >
              Dashboard
            </Link>
            <Link 
              to="/history" 
              className="text-[#718096] hover:text-[#3090A8] transition-colors"
              data-testid="nav-history"
            >
              History
            </Link>
            <Link 
              to="/practices" 
              className="text-[#718096] hover:text-[#3090A8] transition-colors"
              data-testid="nav-library"
            >
              Practice Library
            </Link>
            <Link 
              to="/favorites" 
              className="text-[#718096] hover:text-[#3090A8] transition-colors"
              data-testid="nav-favorites"
            >
              Favorites
            </Link>
            <Link
              to="/faq"
              className="text-[#718096] hover:text-[#3090A8] transition-colors"
              data-testid="nav-faq"
            >
              FAQ
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            <StreakPill current={streak?.current || 0} isMilestoneDay={Boolean(celebratingMilestone)} />
            <DashboardMenu
              stats={stats}
              remindersCount={reminders?.length || 0}
              onOpenReminders={() => setShowReminderDialog(true)}
            />
          </div>
        </div>
      </header>

      {celebratingMilestone && (
        <StreakCelebrationModal
          milestone={celebratingMilestone}
          streak={streak?.current}
          userName={user?.name?.split(" ")[0]}
          onClose={handleCelebrationClose}
        />
      )}

      <main className="max-w-[1440px] xl:max-w-[1680px] mx-auto px-6 lg:px-10 xl:px-14 py-8 lg:py-10 app-prose">
        {/* Welcome Section */}
        <div className="mb-10 lg:mb-12 animate-fade-in-up">
          <h1 className="font-outfit font-semibold text-2xl sm:text-3xl lg:text-4xl text-[#1B2349] mb-2 flex items-center gap-3">
            <span>Welcome back, {user?.name?.split(' ')[0] || 'Friend'}</span>
            <VipBadge user={user} size="lg" />
          </h1>
          <p className="text-[#718096] lg:text-lg">
            Continue your healing journey or start a new practice.
          </p>
        </div>

        {/* Reminder Dialog (opened from dropdown menu) */}
        <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
          <DialogContent className="sm:max-w-md rounded-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-outfit text-[#1B2349] flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#E2B887]" />
                Session Reminders
              </DialogTitle>
              <DialogDescription className="text-[#718096]">
                Manage your scheduled reminders and add new ones.
              </DialogDescription>
            </DialogHeader>

            {/* Enable notifications callout */}
            {notifPermission !== "granted" && (
              <div className="bg-[#EAF0F9] rounded-xl p-3 flex items-center justify-between gap-3">
                <p className="text-xs text-[#1B2349]">Enable browser notifications to be alerted on time.</p>
                <Button
                  onClick={requestNotificationPermission}
                  variant="outline"
                  size="sm"
                  className="rounded-full border-[#78C0D8] text-[#78C0D8] hover:bg-[#78C0D8] hover:text-white text-xs flex-shrink-0"
                  data-testid="enable-notifications-btn"
                >
                  <BellRing className="w-3.5 h-3.5 mr-1.5" />
                  Enable
                </Button>
              </div>
            )}

            {/* Active reminders list */}
            <div>
              <h3 className="text-sm font-medium text-[#1B2349] mb-2">Scheduled</h3>
              {reminders.length > 0 ? (
                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                  {reminders.map((rem) => (
                    <div
                      key={rem.reminder_id}
                      className="bg-[#FBF8F1] rounded-xl p-3 border border-slate-100 flex items-center justify-between"
                      data-testid={`reminder-${rem.reminder_id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 bg-[#FEF3E7] rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-base">{INTERVENTION_ICONS[rem.intervention_type]}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[#1B2349] truncate">
                            {INTERVENTION_TITLES[rem.intervention_type] || rem.intervention_type}
                          </p>
                          <p className="text-xs text-[#718096]">
                            {format(new Date(rem.reminder_time), 'MMM d, yyyy h:mm a')}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteReminder(rem.reminder_id)}
                        className="p-2 rounded-full hover:bg-white transition-colors text-[#D98880] flex-shrink-0"
                        data-testid={`delete-reminder-${rem.reminder_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-xl p-4 border border-dashed border-slate-200 text-center">
                  <p className="text-xs text-[#718096]">
                    No reminders set yet.
                  </p>
                </div>
              )}
            </div>

            {/* Add new reminder form */}
            <div className="pt-2 border-t border-slate-100">
              <h3 className="text-sm font-medium text-[#1B2349] mb-2">Add a new reminder</h3>
              <div className="space-y-3">
                <div>
                  <label htmlFor="reminder-intervention-select" className="text-xs font-medium text-[#718096] block mb-1">Intervention</label>
                  <Select value={reminderType} onValueChange={setReminderType}>
                    <SelectTrigger id="reminder-intervention-select" className="rounded-xl" data-testid="reminder-intervention-select">
                      <SelectValue placeholder="Select a practice" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quiet_the_worry">Quiet The Worry</SelectItem>
                      <SelectItem value="overcome_overwhelm">Overcome Your Overwhelm</SelectItem>
                      <SelectItem value="dial_down_tension">Dial Down Body Tension</SelectItem>
                      <SelectItem value="healing_old_stories">Healing Old Stories</SelectItem>
                      <SelectItem value="beat_stage_fright">Beat Stage Fright</SelectItem>
                      <SelectItem value="fear_of_sleeplessness">Fear Of Sleeplessness</SelectItem>
                      <SelectItem value="shake_off_sadness">Shake Off The Blues</SelectItem>
                      <SelectItem value="boost_self_worth">Boost Your Self-Worth</SelectItem>
                      <SelectItem value="hold_boundaries">Hold Your Boundaries</SelectItem>
                      <SelectItem value="relief_from_grief">Relief From Grief</SelectItem>
                      <SelectItem value="calm_your_anger">Calm Your Anger</SelectItem>
                      <SelectItem value="enhance_performance">Enhance Your Performance</SelectItem>
                      <SelectItem value="new_habit_new_you">New Habit, New You</SelectItem>
                      <SelectItem value="headache_relief">Headache Relief</SelectItem>
                      <SelectItem value="nightmare_reset">Nightmare Reset</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="reminder-date-input" className="text-xs font-medium text-[#718096] block mb-1">Date</label>
                    <Input
                      id="reminder-date-input"
                      type="date"
                      value={reminderDate}
                      onChange={(e) => setReminderDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="rounded-xl"
                      data-testid="reminder-date-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="reminder-time-input" className="text-xs font-medium text-[#718096] block mb-1">Time</label>
                    <Input
                      id="reminder-time-input"
                      type="time"
                      value={reminderTime}
                      onChange={(e) => setReminderTime(e.target.value)}
                      className="rounded-xl"
                      data-testid="reminder-time-input"
                    />
                  </div>
                </div>
                <Button
                  onClick={handleCreateReminder}
                  className="w-full btn-primary"
                  data-testid="confirm-reminder-btn"
                >
                  <CalendarClock className="w-4 h-4 mr-2" />
                  Set Reminder
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Calm Moments this week — shareable wellbeing rollup */}
        <CalmMomentsCard />

        {/* In Progress Section */}
        {stats?.in_progress && stats.in_progress.length > 0 && (
          <div className="mb-10">
            <h2 className="font-outfit font-medium text-lg text-[#1B2349] mb-4 flex items-center gap-2">
              <Pause className="w-5 h-5 text-[#78C0D8]" />
              Continue Where You Left Off
            </h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {stats.in_progress.map((progress) => {
                const intervention = interventions.find(i => i.type === progress.intervention_type);
                if (!intervention) return null;

                const progressPercent = ((progress.current_task - 1) / 9) * 100;
                const visual = INTERVENTION_VISUALS[progress.intervention_type] || {
                  icon: Sparkles, tint: "#3090A8", from: "#C7D9C3", to: "#9EBBA0"
                };
                const ContinueIcon = visual.icon;

                return (
                  <div
                    key={progress.progress_id}
                    className="group/continue relative bg-white rounded-xl overflow-hidden shadow-[0_2px_10px_rgba(0,0,0,0.03)] border border-slate-100 card-hover cursor-pointer"
                    onClick={() => startIntervention(progress.intervention_type)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        startIntervention(progress.intervention_type);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Continue ${progress.intervention_type}`}
                    data-testid={`continue-${progress.intervention_type}`}
                  >
                    {/* Gradient icon panel — mirrors the full intervention tile,
                        compact for the Continue row. */}
                    <div
                      className="aspect-square relative overflow-hidden flex items-center justify-center"
                      style={
                        visual.photo
                          ? { backgroundImage: `url('${visual.photo}')`, backgroundSize: "cover", backgroundPosition: "center" }
                          : { background: `linear-gradient(135deg, ${visual.from}, ${visual.to})` }
                      }
                    >
                      {!visual.photo && (
                        <>
                          <div
                            className="absolute -top-6 -right-6 w-20 h-20 rounded-full opacity-40 blur-2xl pointer-events-none"
                            style={{ background: "rgba(255,255,255,0.7)" }}
                          />
                          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/30 via-black/5 to-transparent pointer-events-none" />

                          <div className="relative z-[1] w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/85 backdrop-blur-sm flex items-center justify-center shadow-[0_4px_16px_rgba(0,0,0,0.10)] transition-transform duration-500 group-hover/continue:scale-110">
                            <ContinueIcon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: visual.tint }} strokeWidth={1.6} />
                          </div>
                        </>
                      )}

                      {/* Delete (abandon) — small ghost X in the top-right */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingProgressDelete(progress);
                        }}
                        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover/continue:opacity-100 hover:bg-white transition-all z-10"
                        aria-label={`Remove ${intervention.title} from in-progress`}
                        data-testid={`remove-progress-${progress.intervention_type}`}
                      >
                        <X className="w-3.5 h-3.5 text-[#718096]" />
                      </button>
                    </div>

                    {/* Compact footer — title + task progress */}
                    <div className="p-2.5">
                      <h3 className="font-outfit font-medium text-[11px] sm:text-xs text-[#1B2349] leading-tight line-clamp-2 mb-0.5">
                        {intervention.title}
                      </h3>
                      <p className="text-[#718096] text-[10px] mb-1.5">
                        Task {progress.current_task}/9
                      </p>
                      <Progress value={progressPercent} className="h-1" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Daily Meditation — auto-rotates through the 3 guided meditations.
            Sits just above the category/practice tiles so it lives inside
            the Daily Practices section. */}
        <DailyMeditationCard />

        {/* Invite a friend tile — sits above the practice grid so it's
            visible without dominating the page. Native share sheet on
            mobile, clipboard fallback elsewhere. */}
        <div className="mt-6 mb-10">
          <ShareInviteButton variant="tile" />
        </div>

        {/* ═══ Category selector + Ferris-wheel carousel ═══
            Mobile: vertical column of chips down the left; desktop: tight
            horizontal bar. Cards are hidden until the user taps a tab. Once
            open, the active category shows a Ferris-wheel-style 3D stack —
            tap a side card to bring it to the front, tap the backdrop to
            dismiss. */}
        {(() => {
          // Trial users can only open the whitelisted set; expired users
          // can open nothing at all. Both paths route to /pricing instead
          // of opening the locked content.
          const isTrialing = billing?.status === "trialing" || billing?.status === "expired";
          const isLocked = (id) => isLockedForUser(id, billing?.status);

          // Library splits into two carousels:
          //   * "library"     → ambient sounds (bilateral + 40 Hz)
          //   * "mindfulness" → the three guided voice meditations
          // Both still resolve to /library/<id> on tap because they share
          // the LibraryPlayer page; only the dashboard filtering differs.
          const isMeditation = (p) => p.category === "Guided Meditation";
          const libraryCardsFor = (tabKey) =>
            (tabKey === "mindfulness"
              ? LIBRARY_PRACTICES.filter(isMeditation)
              : LIBRARY_PRACTICES.filter((p) => !isMeditation(p))
            ).map((p) => ({
              kind: "library",
              id: p.id,
              title: p.title,
              sub: `${p.duration} · ${p.category}`,
              image: p.image,
              locked: isLocked(p.id),
              onOpen: () => isLocked(p.id) ? navigate("/pricing") : navigate(`/library/${p.id}`),
            }));

          const cardsForActive = activeCategory === "library" || activeCategory === "mindfulness"
            ? libraryCardsFor(activeCategory)
            : activeCategory
              ? (groups[activeCategory]?.types || [])
                  .map((t) => interventions.find((i) => i.type === t))
                  .filter(Boolean)
                  .map((iv) => {
                    const v = INTERVENTION_VISUALS[iv.type] || {};
                    const locked = isLocked(iv.type);
                    return {
                      kind: "intervention",
                      id: iv.type,
                      title: iv.title,
                      sub: iv.in_progress ? `Continue · Task ${iv.current_task}/9` : "Tap to begin",
                      image: v.photo || null,
                      visual: v,
                      in_progress: iv.in_progress,
                      is_favorite: iv.is_favorite,
                      intervention: iv,
                      locked,
                      onOpen: () => locked ? navigate("/pricing") : startIntervention(iv.type),
                    };
                  })
              : [];

          const n = cardsForActive.length;
          const safeWheelIndex = n > 0 ? ((wheelIndex % n) + n) % n : 0;
          const frontCard = n > 0 ? cardsForActive[safeWheelIndex] : null;

          // Compute relative offset for each card: -2, -1, 0 (front), 1, 2, etc
          const wheelPositions = cardsForActive.map((c, i) => {
            let rel = i - safeWheelIndex;
            if (rel > n / 2) rel -= n;
            if (rel < -n / 2) rel += n;
            return { c, rel };
          });

          return (
            <div
              className="mb-12"
              data-testid="category-selector"
            >
              {/* ── Category grid: vertical list on mobile, 2-up on tablet,
                  3-up on lg, 6-up on xl so the row fills the dashboard
                  instead of leaving the right side empty. ── */}
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3"
                data-testid="category-pill-bar"
              >
                {CATEGORY_TABS.map((tab) => {
                  const active = tab.key === activeCategory;
                  const count = tab.key === "library"
                    ? LIBRARY_PRACTICES.filter((p) => p.category !== "Guided Meditation").length
                    : tab.key === "mindfulness"
                      ? LIBRARY_PRACTICES.filter((p) => p.category === "Guided Meditation").length
                      : (groups[tab.key]?.types?.length || 0);
                  const TabIcon = tab.icon;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => {
                        if (activeCategory === tab.key) {
                          setActiveCategory(null);  // toggle off
                        } else {
                          setActiveCategory(tab.key);
                          // Land the wheel on the FIRST UNLOCKED card so
                          // trial users see what they CAN open, not a
                          // locked card front-and-center.
                          setWheelIndex(firstUnlockedIndexFor(tab.key));
                        }
                      }}
                      className={`relative flex items-center gap-3 px-4 py-3.5 rounded-2xl border text-left transition-all duration-300 ${
                        active
                          ? "text-white border-transparent shadow-[0_10px_30px_rgba(45,55,72,0.18)]"
                          : "bg-white text-[#4A5568] border-slate-200 hover:border-slate-300 hover:shadow-[0_4px_16px_rgba(45,55,72,0.06)]"
                      }`}
                      style={active ? { backgroundColor: tab.accent } : {}}
                      data-testid={`category-tab-${tab.key}`}
                    >
                      <span
                        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          active ? "bg-white/25" : ""
                        }`}
                        style={active ? {} : { backgroundColor: tab.soft }}
                      >
                        <TabIcon
                          className="w-4 h-4"
                          style={{ color: active ? "#fff" : tab.accent }}
                          strokeWidth={2}
                        />
                      </span>
                      <span className="text-sm font-medium flex-1 leading-tight">
                        {tab.label}
                      </span>
                      <span
                        className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md min-w-[20px] text-center flex-shrink-0 ${
                          active ? "bg-white/25 text-white" : "bg-slate-100 text-[#718096]"
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* ── Ferris-wheel carousel — renders as a fixed overlay above
                  the rest of the dashboard so it floats on top of everything.
                  Tapping the backdrop dismisses it. */}

              {activeCategory && (
                <div
                  className="fixed inset-0 z-40 flex items-center justify-center"
                  data-testid={`wheel-${activeCategory}`}
                  role="button"
                  tabIndex={0}
                  aria-label="Close category carousel"
                  onClick={() => {
                    if (swipeHappened.current) {
                      swipeHappened.current = false;
                      return;
                    }
                    setActiveCategory(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " " || e.key === "Escape") {
                      e.preventDefault();
                      if (swipeHappened.current) {
                        swipeHappened.current = false;
                        return;
                      }
                      setActiveCategory(null);
                    }
                  }}
                  onTouchStart={onWheelTouchStart}
                  onTouchEnd={onWheelTouchEnd}
                  onMouseDown={onWheelTouchStart}
                  onMouseUp={onWheelTouchEnd}
                >
                  {/* Backdrop scrim — soft cream wash so the wheel reads
                      cleanly while the dashboard content shows behind. */}
                  <div className="absolute inset-0 bg-[#FBF8F1]/80 backdrop-blur-sm" />

                  {/* Wheel scene (clicks inside don't dismiss unless they hit
                      the cards' explicit handlers) */}
                  <div
                    ref={wheelStageRef}
                    className="relative w-full max-w-3xl lg:max-w-4xl xl:max-w-5xl h-[360px] lg:h-[440px] xl:h-[520px] select-none touch-pan-y"
                    role="dialog"
                    aria-label="Practice carousel"
                    aria-modal="true"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="relative h-full wheel-stage">
                      {wheelPositions.map(({ c, rel }) => {
                        const abs = Math.abs(rel);
                        const isFront = rel === 0;
                        const x = rel * wheelDims.gap;
                        const z = -abs * 180;
                        const rotY = rel * -22;
                        const scale = isFront ? 1 : 0.82 - Math.min(abs - 1, 2) * 0.08;
                        const opacity = abs > 2 ? 0 : (isFront ? 1 : 0.72 - (abs - 1) * 0.18);
                        const zIndex = 30 - abs;

                        const onCardTap = (e) => {
                          e.stopPropagation();
                          if (isFront) {
                            c.onOpen();
                          } else {
                            setWheelIndex((prev) => prev + rel);
                          }
                        };

                        return (
                          <div
                            key={c.id}
                            onClick={onCardTap}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onCardTap(e);
                              }
                            }}
                            role="button"
                            tabIndex={abs > 2 ? -1 : 0}
                            aria-label={c.title || c.label || "Open card"}
                            className="absolute top-1/2 left-1/2 cursor-pointer transition-all duration-500 ease-out"
                            style={{
                              width: `${wheelDims.card}px`,
                              transform: `translate(-50%, -50%) translate3d(${x}px, 0, ${z}px) rotateY(${rotY}deg) scale(${scale})`,
                              opacity,
                              zIndex,
                              pointerEvents: abs > 2 ? "none" : "auto",
                            }}
                            data-testid={`wheel-card-${c.id}`}
                          >
                            <div className="bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-[0_18px_50px_rgba(45,55,72,0.22)]">
                              {c.kind === "intervention" ? (
                                <>
                                  <div
                                    className="aspect-square relative overflow-hidden flex items-center justify-center"
                                    style={
                                      c.image
                                        ? { backgroundImage: `url('${c.image}')`, backgroundSize: "cover", backgroundPosition: "center" }
                                        : { background: `linear-gradient(135deg, ${c.visual?.from || "#C7D9C3"}, ${c.visual?.to || "#9EBBA0"})` }
                                    }
                                  >
                                    {c.image ? (
                                      <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none" />
                                    ) : (
                                      <>
                                        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/30 via-black/10 to-transparent pointer-events-none" />
                                        <div className="relative z-[1] w-16 h-16 rounded-2xl bg-white/85 backdrop-blur-sm flex items-center justify-center shadow-[0_6px_24px_rgba(0,0,0,0.10)]">
                                          {(() => {
                                            const FallbackIcon = c.visual?.icon || Sparkles;
                                            return <FallbackIcon className="w-8 h-8" style={{ color: c.visual?.tint || "#3090A8" }} strokeWidth={1.6} />;
                                          })()}
                                        </div>
                                      </>
                                    )}

                                    {isFront && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleFavorite(c.id, c.is_favorite);
                                        }}
                                        className="absolute top-2.5 right-2.5 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors z-10"
                                        data-testid={`favorite-btn-${c.id}`}
                                      >
                                        <Heart className={`w-3.5 h-3.5 ${c.is_favorite ? 'fill-[#D98880] text-[#D98880]' : 'text-[#718096]'}`} />
                                      </button>
                                    )}

                                    {c.in_progress && (
                                      <div className="absolute top-2.5 left-2.5 bg-white/90 text-[#1B2349] text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-sm">
                                        {c.intervention.current_task}/9
                                      </div>
                                    )}

                                    {c.locked && (
                                      <div className="absolute inset-0 bg-[#1B2349]/55 backdrop-blur-[1px] flex items-center justify-center pointer-events-none z-[2]">
                                        <div className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-md">
                                          <Lock className="w-5 h-5 text-[#1B2349]" />
                                        </div>
                                      </div>
                                    )}

                                    <div className="absolute bottom-0 left-0 right-0 p-3 z-[3]">
                                      <h3 className="font-outfit font-medium text-white text-sm leading-tight drop-shadow-sm">
                                        {c.title}
                                      </h3>
                                    </div>
                                  </div>
                                  {isFront && (
                                    <div className="p-3">
                                      {c.in_progress && !c.locked && (
                                        <Progress
                                          value={((c.intervention.current_task - 1) / 9) * 100}
                                          className="h-1 mb-2"
                                        />
                                      )}
                                      {c.locked && (
                                        <p className="text-[11px] text-[#718096] text-center mb-1.5 leading-tight">
                                          Unlock now with a subscription. We'll remind you before you're billed.
                                        </p>
                                      )}
                                      <Button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          c.onOpen();
                                        }}
                                        size="sm"
                                        className={`w-full text-xs py-1.5 ${c.locked ? 'btn-secondary' : c.in_progress ? 'btn-secondary' : 'btn-primary'}`}
                                        data-testid={`start-btn-${c.id}`}
                                      >
                                        {c.locked ? (
                                          <>
                                            <Lock className="w-3 h-3 mr-1.5" />
                                            Unlock now
                                          </>
                                        ) : (
                                          <>
                                            <Play className="w-3 h-3 mr-1.5" />
                                            {c.in_progress ? 'Continue' : 'Start'}
                                          </>
                                        )}
                                      </Button>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <>
                                  <div
                                    className="aspect-[4/3] w-full bg-cover bg-center relative"
                                    style={{ backgroundImage: `url('${c.image}')` }}
                                  >
                                    {c.locked && (
                                      <div className="absolute inset-0 bg-[#1B2349]/55 backdrop-blur-[1px] flex items-center justify-center pointer-events-none">
                                        <div className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-md">
                                          <Lock className="w-5 h-5 text-[#1B2349]" />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="p-4">
                                    <div className="flex items-center gap-1.5 text-xs text-[#718096] mb-2">
                                      <Headphones className="w-3.5 h-3.5" />
                                      <span>{c.sub}</span>
                                    </div>
                                    <h3 className="font-outfit font-semibold text-[#1B2349] leading-tight">
                                      {c.title}
                                    </h3>
                                    {isFront && (
                                      c.locked ? (
                                        <div className="mt-3 inline-flex items-center gap-1 text-[#1B2349] text-sm font-medium">
                                          <Lock className="w-3.5 h-3.5" />
                                          Unlock now
                                        </div>
                                      ) : (
                                        <div className="mt-3 inline-flex items-center gap-1 text-[#306090] text-sm font-medium">
                                          Open practice <ChevronRight className="w-3.5 h-3.5" />
                                        </div>
                                      )
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {n > 1 && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            scrollCarousel(-1);
                          }}
                          className="absolute left-2 top-1/2 -translate-y-1/2 z-40 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center hover:bg-slate-50"
                          aria-label="Previous"
                          data-testid="wheel-prev"
                        >
                          <ChevronLeft className="w-5 h-5 text-[#4A5568]" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            scrollCarousel(1);
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 z-40 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center hover:bg-slate-50"
                          aria-label="Next"
                          data-testid="wheel-next"
                        >
                          <ChevronRight className="w-5 h-5 text-[#4A5568]" />
                        </button>

                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-1.5 z-40" data-testid="wheel-dots">
                          {cardsForActive.map((_, i) => (
                            <span
                              key={i}
                              className={`rounded-full transition-all ${
                                i === safeWheelIndex ? "w-4 h-1.5 bg-[#1B2349]" : "w-1.5 h-1.5 bg-slate-300"
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Mobile Navigation */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 px-6 py-3">
          <div className="flex items-center justify-around">
            <Link 
              to="/dashboard"
              className="flex flex-col items-center gap-1 text-[#3090A8]"
            >
              <Sparkles className="w-5 h-5" />
              <span className="text-xs">Home</span>
            </Link>
            <Link 
              to="/history"
              className="flex flex-col items-center gap-1 text-[#718096]"
            >
              <History className="w-5 h-5" />
              <span className="text-xs">History</span>
            </Link>
            <Link 
              to="/favorites"
              className="flex flex-col items-center gap-1 text-[#718096]"
            >
              <Heart className="w-5 h-5" />
              <span className="text-xs">Favorites</span>
            </Link>
            <Link 
              to="/settings"
              className="flex flex-col items-center gap-1 text-[#718096]"
            >
              <Settings className="w-5 h-5" />
              <span className="text-xs">Settings</span>
            </Link>
          </div>
        </nav>
      </main>

      {/* Abandon-progress confirmation dialog — lets users remove a session
          they no longer want to pursue without having to finish it. */}
      <AlertDialog
        open={pendingProgressDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingProgressDelete(null);
        }}
      >
        <AlertDialogContent className="rounded-2xl" data-testid="abandon-progress-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-outfit">
              Clear this in-progress session?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#4A5568] leading-relaxed">
              {pendingProgressDelete && (() => {
                const title = interventions.find(
                  (i) => i.type === pendingProgressDelete.intervention_type
                )?.title || "this intervention";
                return `This removes your current progress on ${title}. Your favourites and completed history are untouched. You can always restart fresh later.`;
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={abandoning} data-testid="abandon-cancel-btn">
              Keep it
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAbandonProgress}
              disabled={abandoning}
              className="bg-[#D98880] hover:bg-[#C8736B] text-white"
              data-testid="abandon-confirm-btn"
            >
              {abandoning ? "Removing…" : "Clear progress"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <SiteFooter />
    </div>
  );
};

export default Dashboard;
